import React, { useState, useEffect, useCallback, useMemo } from "react";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { getDetectionThresholds, updateDetectionThresholds, listPolicies, getPolicy, createPolicy, updatePolicy, uploadPolicyDocument, getPolicyDocumentUrl } from "@/lib/settingsApi";
import { extractApiError } from "@/lib/api";
import DataTable from "@/components/shared/DataTable";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import PdfUploader from "@/components/shared/PdfUploader";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Building2, ShieldCheck, Cpu, Bell, ScrollText, 
  Globe, Mail, Lock, Smartphone, Timer, 
  Map as MapIcon, Zap, Gauge, History, FileLock
} from "lucide-react";

export default function SettingsPage() {
  const { user } = useAuth();
  const frontendRole = user?.frontendRole;
  if (frontendRole !== "super_admin") {
    return (
      <div className="space-y-6 pb-12" data-testid="settings-page-unauthorized">
        <SectionCard title="Access Denied" subtitle="You do not have permission to view this page.">
          <div className="text-sm text-slate-600">You must be a platform super admin to access System Settings.</div>
        </SectionCard>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-12" data-testid="settings-page">
      <div className="flex items-end justify-between">
        <PageHeader 
          title="System Settings" 
          subtitle="Manage your organization, platform security, and operational compliance." 
        />
      </div>

      <Tabs defaultValue="system" className="w-full">
        <TabsList className="w-full justify-start border-b rounded-none h-auto p-0 bg-transparent mb-6">
          {/* <TabsTrigger value="general" className="rounded-none border-b-2 border-transparent data-[state=active]:border-slate-900 data-[state=active]:bg-transparent px-6 py-3">
            <Building2 size={16} className="mr-2" /> General
          </TabsTrigger>
          <TabsTrigger value="security" className="rounded-none border-b-2 border-transparent data-[state=active]:border-slate-900 data-[state=active]:bg-transparent px-6 py-3">
            <ShieldCheck size={16} className="mr-2" /> Security
          </TabsTrigger> */}
          <TabsTrigger value="system" className="rounded-none border-b-2 border-transparent data-[state=active]:border-slate-900 data-[state=active]:bg-transparent px-6 py-3">
            <Cpu size={16} className="mr-2" /> System
          </TabsTrigger>
          <TabsTrigger value="notifications" className="rounded-none border-b-2 border-transparent data-[state=active]:border-slate-900 data-[state=active]:bg-transparent px-6 py-3">
            <Bell size={16} className="mr-2" /> Notifications
          </TabsTrigger>
          <TabsTrigger value="legal" className="rounded-none border-b-2 border-transparent data-[state=active]:border-slate-900 data-[state=active]:bg-transparent px-6 py-3">
            <ScrollText size={16} className="mr-2" /> Legal & Policies
          </TabsTrigger>
        </TabsList>

        {/* General Settings */}
        {/* <TabsContent value="general" className="space-y-6 outline-none">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <SectionCard title="Organization Profile" subtitle="Public identity of your command center.">
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-slate-600">Organization Name</Label>
                  <Input defaultValue="Reyansh Technologies" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-slate-600 flex items-center gap-2"><Globe size={14} /> Region</Label>
                    <Input defaultValue={user?.region || "National Headquarters"} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-slate-600 flex items-center gap-2"><Mail size={14} /> Ops Email</Label>
                    <Input defaultValue={user?.email || "ops@iiotsafety.io"} />
                  </div>
                </div>
                <Button className="w-full bg-slate-900 text-white" onClick={() => handleSave("Profile")}>Save Profile</Button>
              </div>
            </SectionCard>

            <SectionCard title="Platform Branding" subtitle="Customize the dashboard look and feel.">
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 rounded-xl border border-slate-100 bg-slate-50/50">
                  <div className="space-y-0.5">
                    <p className="text-sm font-medium">Dark Mode Default</p>
                    <p className="text-xs text-slate-500">Enable high-contrast dark theme by default for all users.</p>
                  </div>
                  <Switch />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-slate-600">Primary Brand Color</Label>
                  <div className="flex gap-2">
                    <div className="h-10 w-full rounded-md border border-slate-200 bg-[#7c3aed] flex items-center justify-center text-white text-xs font-mono">#7C3AED</div>
                    <Button variant="outline">Change</Button>
                  </div>
                </div>
              </div>
            </SectionCard>
          </div>
        </TabsContent> */}

        {/* Security Settings */}
        {/* <TabsContent value="security" className="space-y-6 outline-none">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <SectionCard title="Access Control" subtitle="Manage authentication requirements.">
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 rounded-xl border border-slate-100 bg-slate-50/50">
                  <div className="space-y-0.5">
                    <div className="flex items-center gap-2">
                      <Smartphone size={14} className="text-blue-600" />
                      <p className="text-sm font-medium">Mandatory 2FA</p>
                    </div>
                    <p className="text-xs text-slate-500">Require all admins to use an authenticator app.</p>
                  </div>
                  <Switch defaultChecked />
                </div>
                <div className="flex items-center justify-between p-3 rounded-xl border border-slate-100 bg-slate-50/50">
                  <div className="space-y-0.5">
                    <div className="flex items-center gap-2">
                      <Timer size={14} className="text-blue-600" />
                      <p className="text-sm font-medium">Auto Session Timeout</p>
                    </div>
                    <p className="text-xs text-slate-500">Automatically logout inactive users after 30 minutes.</p>
                  </div>
                  <Switch defaultChecked />
                </div>
                <Separator />
                <div className="space-y-1.5">
                  <Label className="text-slate-600">Minimum Password Length</Label>
                  <Input type="number" defaultValue={12} />
                </div>
              </div>
            </SectionCard>

            <SectionCard title="Security Logs & Audits" subtitle="Track administrative changes.">
              <div className="space-y-4">
                <div className="p-3 rounded-xl border border-amber-100 bg-amber-50/30">
                  <p className="text-xs font-medium text-amber-800">Critical: There have been 3 failed login attempts from unknown IPs in the last 24 hours.</p>
                </div>
                {canReadAudit ? (
                  <Button variant="outline" className="w-full flex items-center justify-center gap-2">
                    <History size={16} /> View Audit Logs
                  </Button>
                ) : null}
                <Button variant="outline" className="w-full flex items-center justify-center gap-2">
                  <FileLock size={16} /> Export Security Report
                </Button>
              </div>
            </SectionCard>
          </div>
        </TabsContent> */}

        {/* System Settings */}
        <TabsContent value="system" className="space-y-6 outline-none">
          <SectionCard title="Operational Thresholds" subtitle="Configure sensitivity for automated safety alerts.">
            <DetectionThresholdsForm />
          </SectionCard>

          {/* <SectionCard title="API & Integrations" subtitle="Connect external services to the platform.">
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Mapbox Access Token</Label>
                <Input type="password" value="sk.eyJ1IjoicmV5YW5zaC10ZWNoIiwiYSI6ImNsd" readOnly />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Button variant="outline">Test Connection</Button>
                <Button variant="outline">Rotate Keys</Button>
              </div>
            </div>
          </SectionCard> */}
        </TabsContent>

        {/* Notifications Settings */}
        <TabsContent value="notifications" className="space-y-6 outline-none">
          <SectionCard title="Automated Dispatch & Alerts" subtitle="Control system-wide notification behavior.">
            <div className="space-y-3">
              {[
                { label: "Critical Incidents", desc: "Push high-priority alerts to all connected service dashboards.", checked: true },
                { label: "Daily Digest", desc: "Send summary of previous day operations to operations head at 06:00.", checked: true },
                { label: "SMS Fallback", desc: "Automatically send SMS to officers when internet connection is lost.", checked: false },
                { label: "Maintenance Alerts", desc: "Notify service teams when IoT devices show low battery or connectivity.", checked: true },
              ].map((item, i) => (
                <div key={i} className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50/40 p-4">
                  <div className="space-y-0.5">
                    <p className="text-sm font-semibold text-slate-900">{item.label}</p>
                    <p className="text-xs text-slate-500">{item.desc}</p>
                  </div>
                  <Switch defaultChecked={item.checked} />
                </div>
              ))}
            </div>
          </SectionCard>
        </TabsContent>

        {/* Legal & Policies Settings */}
        <TabsContent value="legal" className="space-y-6 outline-none">
          <div className="grid grid-cols-1 ">
            <SectionCard title="Platform Policies" subtitle="Managed legal documentation and compliance.">
                  <PoliciesSection />
            </SectionCard>

            {/* <SectionCard title="Data Management" subtitle="Compliance with regional data protection laws.">
              <div className="space-y-4 text-sm text-slate-600">
                <p>Ensuring compliance with local safety regulations and electronic monitoring acts.</p>
                <div className="p-4 rounded-2xl bg-blue-50/50 border border-blue-100 space-y-2">
                  <div className="flex items-center gap-2 text-blue-900 font-semibold">
                    <ScrollText size={14} />
                    <p className="text-xs uppercase tracking-widest">Compliance Summary</p>
                  </div>
                  <p className="text-xs leading-relaxed">System is currently compliant with **IEC 61508** and **ISO 26262** functional safety standards for emergency IoT systems.</p>
                </div>
                <div className="grid grid-cols-1 gap-2">
                  <Button variant="outline" className="justify-start">Download Compliance Certificate</Button>
                  <Button variant="outline" className="justify-start">Run Data Audit</Button>
                </div>
              </div>
            </SectionCard> */}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function numOrNull(value) {
  const n = Number(String(value).trim());
  return Number.isFinite(n) ? n : null;
}

function DetectionThresholdsForm() {
  const [accessDenied, setAccessDenied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [values, setValues] = useState({});
  const [original, setOriginal] = useState({});
  const [errors, setErrors] = useState({});
  const [dirty, setDirty] = useState(new Set());
  const [fieldKeys, setFieldKeys] = useState([]);

  const fieldLabel = useCallback((key) => {
    const unit = key.endsWith("_kmh") ? " (km/h)" : key.endsWith("_c") ? " (°C)" : key.endsWith("_seconds") ? " (seconds)" : "";
    return key
      .replace(/^threshold_/, "")
      .replace(/^incident_/, "")
      .replace(/_kmh$|_c$|_seconds$/, "")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase()) + unit;
  }, []);

  const fieldGroup = useCallback((key) => {
    const k = String(key || "").toLowerCase();
    if (k.includes("fire")) return "Fire";
    if (k.includes("speed")) return "Speed";
    if (k.includes("coolant") || k.includes("temp")) return "Temperature";
    if (k.includes("passenger")) return "Passenger";
    if (k.includes("dedup")) return "Dedup";
    return "Other";
  }, []);

  const groupedFields = useMemo(() => {
    const buckets = {
      Speed: [],
      Temperature: [],
      Fire: [],
      Passenger: [],
      Dedup: [],
      Other: [],
    };

    fieldKeys.forEach((key) => {
      const g = fieldGroup(key);
      buckets[g] = buckets[g] || [];
      buckets[g].push(key);
    });

    const order = ["Speed", "Temperature", "Fire", "Passenger", "Dedup", "Other"];
    return order
      .map((name) => ({ name, keys: buckets[name] || [] }))
      .filter((group) => group.keys.length > 0);
  }, [fieldGroup, fieldKeys]);

  const load = useCallback(async () => {
    setLoading(true);
    setAccessDenied(false);
    try {
      const resp = await getDetectionThresholds();
      const data = resp?.data || resp || {};
      const keys = Object.keys(data).filter((k) => {
        const v = data[k];
        return v === null || ["number", "string"].includes(typeof v);
      });
      // store as strings for controlled inputs
      const asStrings = {};
      keys.forEach((k) => {
        const v = data[k];
        asStrings[k] = v === null || v === undefined ? "" : String(v);
      });
      setFieldKeys(keys);
      setValues(asStrings);
      setOriginal(asStrings);
      setDirty(new Set());
      setErrors({});
    } catch (err) {
      if (err?.response?.status === 403) {
        setAccessDenied(true);
      } else {
        const apiErr = extractApiError(err);
        toast.error(apiErr.message || "Failed to load thresholds.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const markDirtyForKey = useCallback((key, nextVal) => {
    setDirty((prev) => {
      const next = new Set(prev);
      const orig = original[key] ?? "";
      if (String(nextVal) !== String(orig)) next.add(key);
      else next.delete(key);
      return next;
    });
  }, [original]);

  const handleChange = (key, raw) => {
    setValues((prev) => ({ ...prev, [key]: raw }));
    // don't validate on every keystroke; just track dirty
    markDirtyForKey(key, raw);
  };

  const validateField = (key, raw, allValues) => {
    const v = String(raw).trim();
    if (v === "") return ""; // allow empty (server may enforce)
    const n = Number(v);
    if (!Number.isFinite(n)) return "Must be a number.";

    const between = (min, max) => (n < min || n > max) ? `Must be between ${min} and ${max}.` : "";

    if (key === "threshold_speed_kmh" || key === "threshold_speed_drop_kmh" || key === "threshold_speed_drop_floor_kmh") {
      return between(20, 200);
    }
    if (key === "threshold_consecutive_speed_readings") return between(1, 10);
    if (key === "threshold_coolant_medium_c" || key === "threshold_coolant_critical_c" || key === "threshold_engine_temp_medium_c" || key === "threshold_engine_temp_critical_c") {
      return between(80, 160);
    }
    if (key === "threshold_consecutive_temp_readings") return between(1, 10);
    if (key === "threshold_fire_temp_c") return between(120, 250);
    if (key === "threshold_passenger_overload") return between(1, 100);
    if (key === "incident_dedup_seconds") return between(10, 3600);
    return "";
  };

  const handleBlur = (key) => {
    setErrors((prev) => ({ ...prev, [key]: validateField(key, values[key], values) }));
  };

  const validateAll = (vals) => {
    const next = {};
    fieldKeys.forEach((k) => {
      const msg = validateField(k, vals[k], vals);
      if (msg) next[k] = msg;
    });

    // cross-field checks
    const coolantMedium = numOrNull(vals.threshold_coolant_medium_c);
    const coolantCritical = numOrNull(vals.threshold_coolant_critical_c);
    const engineMedium = numOrNull(vals.threshold_engine_temp_medium_c);
    const engineCritical = numOrNull(vals.threshold_engine_temp_critical_c);
    const fireTemp = numOrNull(vals.threshold_fire_temp_c);
    if (coolantMedium !== null && coolantCritical !== null && fireTemp !== null) {
      if (!(coolantMedium < coolantCritical && coolantCritical < fireTemp)) {
        next.threshold_coolant_medium_c = next.threshold_coolant_medium_c || "Must satisfy medium < critical < fire temp.";
        next.threshold_coolant_critical_c = next.threshold_coolant_critical_c || "Must satisfy medium < critical < fire temp.";
        next.threshold_fire_temp_c = next.threshold_fire_temp_c || "Must satisfy medium < critical < fire temp.";
      }
    }
    if (engineMedium !== null && engineCritical !== null && fireTemp !== null) {
      if (!(engineMedium < engineCritical && engineCritical < fireTemp)) {
        next.threshold_engine_temp_medium_c = next.threshold_engine_temp_medium_c || "Must satisfy medium < critical < fire temp.";
        next.threshold_engine_temp_critical_c = next.threshold_engine_temp_critical_c || "Must satisfy medium < critical < fire temp.";
        next.threshold_fire_temp_c = next.threshold_fire_temp_c || "Must satisfy medium < critical < fire temp.";
      }
    }

    const dropFloor = numOrNull(vals.threshold_speed_drop_floor_kmh);
    const drop = numOrNull(vals.threshold_speed_drop_kmh);
    if (dropFloor !== null && drop !== null) {
      if (!(dropFloor < drop)) {
        next.threshold_speed_drop_floor_kmh = next.threshold_speed_drop_floor_kmh || "Must be less than speed drop.";
        next.threshold_speed_drop_kmh = next.threshold_speed_drop_kmh || "Must be greater than drop floor.";
      }
    }

    return next;
  };

  const handleSave = async () => {
    const changed = Array.from(dirty);
    if (!changed.length) return;
    const validation = validateAll(values);
    if (Object.keys(validation).length) {
      setErrors(validation);
      toast.error("Please fix validation errors before saving.");
      return;
    }

    const payload = {};
    changed.forEach((k) => {
      const n = numOrNull(values[k]);
      if (n === null) payload[k] = null; else payload[k] = n;
    });

    setSaving(true);
    try {
      await updateDetectionThresholds(payload);
      toast.success("Thresholds updated — live within ~30s");
      await load();
    } catch (err) {
      const apiErr = extractApiError(err);
      // field-level errors handling
      const details = apiErr.details || {};
      if (details.fields && Array.isArray(details.fields)) {
        const fieldMap = {};
        details.fields.forEach((f) => {
          if (f?.field) fieldMap[f.field] = f.message || f.type || "Invalid value";
        });
        setErrors(fieldMap);
      } else {
        toast.error(apiErr.message || "Failed to save thresholds.");
      }
    } finally {
      setSaving(false);
    }
  };

  const renderNumeric = (key, label, hint) => (
    <div className="space-y-1.5">
      <Label className="text-slate-600">{label}</Label>
      <Input
        type="number"
        value={values[key] ?? ""}
        onChange={(e) => handleChange(key, e.target.value)}
        onBlur={() => handleBlur(key)}
      />
      {hint ? <p className="text-xs text-slate-500">{hint}</p> : null}
      {errors[key] ? <p className="text-xs text-rose-600">{errors[key]}</p> : null}
    </div>
  );

  if (accessDenied) {
    return (
      <div className="space-y-4">
        <SectionCard title="Access Denied" subtitle="You do not have permission to view Detection Thresholds.">
          <div className="text-sm text-slate-600">You must be a platform super admin with proper privileges to view these settings.</div>
        </SectionCard>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {loading ? (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <div className="space-y-3">
            <Skeleton className="h-6 w-40" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
          <div className="space-y-3">
            <Skeleton className="h-6 w-28" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
          <div className="space-y-3">
            <Skeleton className="h-6 w-20" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {groupedFields.map((group) => (
          <div key={group.name} className="space-y-4">
            <p className="text-sm font-semibold">{group.name}</p>
            {group.keys.map((key) => renderNumeric(key, fieldLabel(key)))}
          </div>
        ))}
      </div>

      {!loading && groupedFields.length === 0 ? (
        <div className="text-sm text-slate-500">No detection parameters returned by API.</div>
      ) : null}

      <div className="flex items-center gap-2">
        <Button className="bg-slate-900 text-white" onClick={handleSave} disabled={saving || dirty.size === 0}>
          {saving ? "Saving..." : "Save"}
        </Button>
        <Button variant="outline" size="sm" disabled={dirty.size === 0} onClick={() => { setValues(original); setDirty(new Set()); setErrors({}); }}>
          Reset
        </Button>
      </div>
    </div>
  );
}

function formatDateTime(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("en-IN", { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function StatusBadge({ status }) {
  const upper = String(status || "").toUpperCase();
  const mapping = {
    ACTIVE: "bg-emerald-100 text-emerald-700",
    NEEDS_REVIEW: "bg-amber-100 text-amber-700",
    DRAFT: "bg-slate-100 text-slate-600",
    ARCHIVED: "bg-stone-50 text-stone-500",
  };
  const cls = mapping[upper] || "bg-slate-100 text-slate-600";
  return <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-tight ${cls}`}>{upper}</span>;
}

function PoliciesSection() {
  const [accessDenied, setAccessDenied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [policies, setPolicies] = useState([]);
  const [error, setError] = useState("");

  const [newOpen, setNewOpen] = useState(false);
  const [newSubmitting, setNewSubmitting] = useState(false);
  const [newForm, setNewForm] = useState({ slug: "", title: "", status: "DRAFT", body: "" });
  const [newErrors, setNewErrors] = useState({});

  const [editOpen, setEditOpen] = useState(false);
  const [editSlug, setEditSlug] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editForm, setEditForm] = useState({ slug: "", title: "", status: "", body: "" });
  const [editErrors, setEditErrors] = useState({});
  const [editHasDocument, setEditHasDocument] = useState(false);
  const [editDocumentFilename, setEditDocumentFilename] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const resp = await listPolicies();
      const items = resp?.data || resp || [];
      setPolicies(Array.isArray(items) ? items : []);
    } catch (err) {
      if (err?.response?.status === 403) {
        setAccessDenied(true);
      } else {
        const apiErr = extractApiError(err);
        setError(apiErr.message || "Failed to load policies.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleOpenEdit = async (row) => {
    const slug = row?.slug;
    if (!slug) return;
    setEditOpen(true);
    setEditSlug(slug);
    setEditLoading(true);
    setEditErrors({});
    try {
      const resp = await getPolicy(slug);
      const data = resp?.data || resp || {};
      setEditForm({ slug: data.slug || slug, title: data.title || "", status: data.status || "DRAFT", body: data.body || "" });
      const doc = data.document || null;
      const hasDoc = Boolean(data.has_document);
      const filename = doc?.filename || (hasDoc ? "Document uploaded" : null);
      setEditHasDocument(hasDoc);
      setEditDocumentFilename(filename || null);
    } catch (err) {
      const apiErr = extractApiError(err);
      toast.error(apiErr.message || "Failed to load policy.");
      setEditOpen(false);
    } finally {
      setEditLoading(false);
    }
  };

  const handleCreate = async () => {
    setNewErrors({});
    setNewSubmitting(true);
    try {
      await createPolicy(newForm);
      toast.success("Policy created");
      setNewOpen(false);
      setNewForm({ slug: "", title: "", status: "DRAFT", body: "" });
      await load();
    } catch (err) {
      const apiErr = extractApiError(err);
      const status = err?.response?.status;
      if (status === 409) {
        setNewErrors({ slug: apiErr.message || "Slug already exists." });
      } else if (apiErr.details?.fields && Array.isArray(apiErr.details.fields)) {
        const map = {};
        apiErr.details.fields.forEach((f) => { if (f?.field) map[f.field] = f.message || f.type || "Invalid"; });
        setNewErrors(map);
      } else {
        toast.error(apiErr.message || "Failed to create policy.");
      }
    } finally {
      setNewSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    setEditErrors({});
    setEditSubmitting(true);
    try {
      await updatePolicy(editSlug, { title: editForm.title, status: editForm.status, body: editForm.body });
      toast.success("Policy updated");
      setEditOpen(false);
      await load();
    } catch (err) {
      const apiErr = extractApiError(err);
      const status = err?.response?.status;
      if (status === 409) {
        setEditErrors({ slug: apiErr.message || "Conflict" });
      } else if (apiErr.details?.fields && Array.isArray(apiErr.details.fields)) {
        const map = {};
        apiErr.details.fields.forEach((f) => { if (f?.field) map[f.field] = f.message || f.type || "Invalid"; });
        setEditErrors(map);
      } else {
        toast.error(apiErr.message || "Failed to update policy.");
      }
    } finally {
      setEditSubmitting(false);
    }
  };

  const columns = [
    { key: "title", label: "Title", render: (r) => r.title || "-" },
    { key: "slug", label: "Slug", render: (r) => r.slug || "-" },
    { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
    { key: "has_document", label: "Doc", render: (r) => (r.has_document ? "Yes" : "No") },
    { key: "updated_at", label: "Updated", render: (r) => formatDateTime(r.updated_at) },
  ];

  if (accessDenied) {
    return (
      <div className="space-y-3">
        <SectionCard title="Access Denied" subtitle="You do not have permission to view Platform Policies.">
          <div className="text-sm text-slate-600">You must be a platform super admin with proper privileges to view these settings.</div>
        </SectionCard>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div />
        <div>
          <Button onClick={() => setNewOpen(true)}>
            New Policy
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-3">
        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : null}

        {error ? <div className="text-rose-600">{error}</div> : null}

        {!loading && !error && policies.length === 0 ? (
          <div className="p-4 text-sm text-slate-600">
            No policies found. Use "New Policy" to create one.
          </div>
        ) : (
          <DataTable columns={columns} data={policies} loading={loading} onRowClick={handleOpenEdit} />
        )}
      </div>

      {/* New Policy Dialog */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>New Policy</DialogTitle>
            <DialogDescription>Create a new platform policy</DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label>Slug</Label>
              <Input value={newForm.slug} onChange={(e) => setNewForm((p) => ({ ...p, slug: e.target.value }))} />
              {newErrors.slug ? <p className="text-xs text-rose-600">{newErrors.slug}</p> : null}
            </div>
            <div>
              <Label>Title</Label>
              <Input value={newForm.title} onChange={(e) => setNewForm((p) => ({ ...p, title: e.target.value }))} />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={newForm.status} onValueChange={(v) => setNewForm((p) => ({ ...p, status: v }))}>
                <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="DRAFT">DRAFT</SelectItem>
                  <SelectItem value="ACTIVE">ACTIVE</SelectItem>
                  <SelectItem value="NEEDS_REVIEW">NEEDS_REVIEW</SelectItem>
                  <SelectItem value="ARCHIVED">ARCHIVED</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Body (optional)</Label>
              <Textarea value={newForm.body} onChange={(e) => setNewForm((p) => ({ ...p, body: e.target.value }))} />
            </div>

            <div className="flex items-center gap-2">
              <Button onClick={handleCreate} disabled={newSubmitting}>{newSubmitting ? 'Saving...' : 'Create'}</Button>
              <Button variant="outline" onClick={() => setNewOpen(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Policy Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Policy</DialogTitle>
            <DialogDescription>Edit policy details</DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {editLoading ? <div className="text-sm text-slate-500">Loading...</div> : (
              <>
                <div>
                  <Label>Slug</Label>
                  <Input value={editForm.slug} readOnly />
                  {editErrors.slug ? <p className="text-xs text-rose-600">{editErrors.slug}</p> : null}
                </div>
                <div>
                  <Label>Title</Label>
                  <Input value={editForm.title} onChange={(e) => setEditForm((p) => ({ ...p, title: e.target.value }))} />
                </div>
                <div>
                  <Label>Body</Label>
                  <Textarea value={editForm.body} onChange={(e) => setEditForm((p) => ({ ...p, body: e.target.value }))} />
                </div>

                <div>
                  <Label>Document</Label>
                  <div className="space-y-2">
                    <PdfUploader
                      currentFilename={editDocumentFilename}
                      disabled={editSubmitting}
                      onUpload={async (file) => {
                        await uploadPolicyDocument(editSlug, file);
                        // refresh policy details
                        const refreshed = await getPolicy(editSlug);
                        const dataRef = refreshed?.data || refreshed || {};
                        const docRef = dataRef.document || null;
                        const hasDocRef = Boolean(dataRef.has_document);
                        const filenameRef = docRef?.filename || (hasDocRef ? "Document uploaded" : null);
                        setEditHasDocument(hasDocRef);
                        setEditDocumentFilename(filenameRef || null);
                        toast.success("Document uploaded");
                      }}
                    />

                    {editHasDocument ? (
                      <div className="flex items-center gap-2">
                        <Button variant="link" onClick={async () => {
                          try {
                            const resp = await getPolicyDocumentUrl(editSlug);
                            const payload = resp?.data || resp || {};
                            const url = payload.url || payload.presigned_url || payload.upload_url || null;
                            if (url) window.open(url, "_blank", "noopener");
                          } catch (err) {
                            const apiErr = extractApiError(err);
                            toast.error(apiErr.message || "Failed to fetch document URL.");
                          }
                        }}>Download current PDF</Button>
                        <span className="text-xs text-slate-500">{editDocumentFilename || "Document uploaded"}</span>
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="mt-2">
                  <Label>Status</Label>
                  <div>
                    <Select value={editForm.status} onValueChange={(v) => setEditForm((p) => ({ ...p, status: v }))}>
                      <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="DRAFT">DRAFT</SelectItem>
                        <SelectItem value="ACTIVE">ACTIVE</SelectItem>
                        <SelectItem value="NEEDS_REVIEW">NEEDS_REVIEW</SelectItem>
                        <SelectItem value="ARCHIVED">ARCHIVED</SelectItem>
                      </SelectContent>
                    </Select>
                    {editForm.status === "ACTIVE" && !editHasDocument ? (
                      <p className="text-xs text-amber-700 mt-1">Warning: policies set to ACTIVE are public; consider uploading a document before publishing.</p>
                    ) : null}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button onClick={handleUpdate} disabled={editSubmitting}>{editSubmitting ? 'Saving...' : 'Save'}</Button>
                  <Button variant="outline" onClick={() => setEditOpen(false)}>Close</Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
